import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, Plus, ShieldCheck, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const ROLE_LABELS = {
  super_admin: "Super admin",
  verifier: "Verifier",
  support: "Support",
};

export default function AdminUsersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "verifier" });
  const [initialPw, setInitialPw] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load admins"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/admin/admins", form);
      setInitialPw(data.temporary_password);
      setForm({ email: "", full_name: "", role: "verifier" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Create failed"));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/admins/${row.id}`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Deactivated" : "Reactivated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    }
  };

  const changeRole = async (row, next) => {
    try {
      await api.patch(`/admin/admins/${row.id}`, { role: next });
      toast.success("Role updated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-12 md:px-10" data-testid="admin-users-page">
      <div className="mb-rise flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/15 bg-brand-navy/5 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
            <ShieldCheck className="h-3 w-3" />
            Admin users
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Team access
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500">
            Sub-admin accounts with scoped permissions. Deactivating an
            admin prevents login immediately.
          </p>
        </div>
        <Button
          className="mb-focus-ring rounded-full bg-brand-navy text-white hover:bg-brand-navy-hover"
          onClick={() => { setInitialPw(null); setOpenCreate(true); }}
          data-testid="admin-create-open"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          New admin
        </Button>
      </div>

      <div className="mt-10 rounded-2xl border border-gray-100 bg-white shadow-mb-card">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">
            No admin users yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="border-b border-gray-100 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Last login</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100" data-testid={`admin-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-800">{r.email}</td>
                  <td className="px-4 py-3">{r.full_name}</td>
                  <td className="px-4 py-3">
                    <Select value={r.role} onValueChange={(v) => changeRole(r, v)}>
                      <SelectTrigger
                        className="h-8 w-40 text-xs"
                        data-testid={`admin-role-select-${r.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLE_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={r.is_active
                      ? "inline-flex items-center rounded-full bg-brand-green/10 px-2.5 py-0.5 text-xs font-semibold text-brand-green"
                      : "inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500"}>
                      {r.is_active ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {r.last_login_at ? new Date(r.last_login_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(r)}
                      data-testid={`admin-toggle-active-${r.id}`}
                    >
                      {r.is_active ? "Deactivate" : "Reactivate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (!o) setInitialPw(null); }}>
        <DialogContent className="max-w-md" data-testid="admin-create-dialog">
          <DialogHeader>
            <DialogTitle>Create sub-admin</DialogTitle>
            <DialogDescription>
              Passwords are shown once — copy and share via a secure channel.
            </DialogDescription>
          </DialogHeader>
          {initialPw ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-brand-green/25 bg-brand-green/5 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-green">Initial password</div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <code className="rounded bg-white px-2 py-1 font-mono text-sm" data-testid="admin-initial-pw">{initialPw}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { navigator.clipboard.writeText(initialPw); toast.success("Copied"); }}
                    data-testid="admin-initial-pw-copy"
                  >
                    <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
                  </Button>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  User will be forced to change this on first login.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => { setInitialPw(null); setOpenCreate(false); }} data-testid="admin-create-done">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitCreate} className="space-y-4" data-testid="admin-create-form">
              <div>
                <Label htmlFor="cf-email">Email</Label>
                <Input id="cf-email" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="admin-create-email" />
              </div>
              <div>
                <Label htmlFor="cf-name">Full name</Label>
                <Input id="cf-name" required value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="admin-create-name" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="admin-create-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={creating} data-testid="admin-create-submit">
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserCog className="mr-2 h-4 w-4" />}
                  Create admin
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
